import { NavLink, useLocation } from "react-router-dom";
import clsx from "clsx";
import { SiteBrandMark } from "@/components/branding/SiteBrandMark";
import {
  ALL_NAV_ITEMS,
  MORE_MENU_GROUPS,
  SIDEBAR_PRIMARY,
  STAMMDATEN_NAV,
  isNavItemActive,
} from "@/lib/nav";
import { NavBadge } from "@/navigation/NavBadge";
import { NavIcon } from "@/navigation/NavIcon";
import { useNav } from "@/navigation/navContext";
import { useNavAccess, useNavGroupAccess } from "@/navigation/useNavAccess";
import { PanelLeftClose, PanelLeftOpen, Search, Star } from "lucide-react";

function SidebarLink({
  href,
  label,
  icon,
  badgeKey,
  collapsed,
  description,
}: {
  href: string;
  label: string;
  icon?: import("@/lib/nav").NavIconName;
  badgeKey?: import("@/lib/nav").NavItem["badgeKey"];
  collapsed: boolean;
  description?: string;
}) {
  const { pathname } = useLocation();
  const { badges, toggleFavorite, favoriteHrefs } = useNav();
  const active = isNavItemActive(pathname, href);
  const isFavorite = favoriteHrefs.includes(href);

  return (
    <div className="group flex items-center">
      <NavLink
        to={href}
        end={href === "/"}
        title={collapsed ? label : description ?? label}
        className={clsx(
          "flex min-h-[2.375rem] flex-1 items-center gap-2.5 rounded-lg px-2.5 text-sm font-medium transition-colors",
          active
            ? "bg-accent text-label-on-accent shadow-sm"
            : "text-label-secondary hover:bg-fill hover:text-label",
          collapsed && "justify-center px-2",
        )}
      >
        {icon ? (
          <NavIcon
            name={icon}
            size={18}
            className={active ? "text-label-on-accent" : undefined}
          />
        ) : null}
        {!collapsed ? (
          <>
            <span className="min-w-0 flex-1 truncate">{label}</span>
            <div className="flex shrink-0 items-center gap-0.5">
              <NavBadge
                item={{ key: href, label, href, badgeKey }}
                badges={badges}
                active={active}
              />
              <button
                type="button"
                className={clsx(
                  "rounded p-0.5 opacity-0 transition group-hover:opacity-100",
                  isFavorite ? "text-status-yellow opacity-100" : "text-label-tertiary hover:text-label",
                  active && !isFavorite && "text-label-on-accent/70 hover:text-label-on-accent",
                )}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  toggleFavorite(href);
                }}
                aria-label={isFavorite ? "Aus Favoriten entfernen" : "Zu Favoriten hinzufügen"}
              >
                <Star size={14} fill={isFavorite ? "currentColor" : "none"} />
              </button>
            </div>
          </>
        ) : null}
      </NavLink>
    </div>
  );
}

export function AppSidebar() {
  const {
    sidebarCollapsed,
    toggleSidebar,
    openCommandPalette,
    recentHrefs,
    favoriteHrefs,
  } = useNav();
  const primary = useNavAccess(SIDEBAR_PRIMARY);
  const moreGroups = useNavGroupAccess(MORE_MENU_GROUPS);

  const favoriteItems = favoriteHrefs
    .map((href) => {
      const main = ALL_NAV_ITEMS.find((i) => i.href === href);
      if (main) return main;
      return STAMMDATEN_NAV.find((i) => i.href === href);
    })
    .filter((item, idx, arr): item is NonNullable<typeof item> => {
      if (!item) return false;
      return arr.findIndex((x) => x?.key === item.key) === idx;
    });

  const recentItems = recentHrefs
    .filter((href) => !favoriteHrefs.includes(href))
    .map((href) => {
      const exact = ALL_NAV_ITEMS.find((i) => i.href === href);
      if (exact) return exact;
      if (href.startsWith("/stammdaten")) {
        return STAMMDATEN_NAV.find((i) => href === i.href || href.startsWith(`${i.href}/`));
      }
      return ALL_NAV_ITEMS.find((i) => href.startsWith(i.href));
    })
    .filter((item, idx, arr): item is NonNullable<typeof item> => {
      if (!item) return false;
      return arr.findIndex((x) => x?.key === item.key) === idx;
    })
    .slice(0, 4);

  return (
    <aside
      data-tour="sidebar"
      className={clsx(
        "eh-sidebar hidden shrink-0 flex-col border-r border-separator bg-app lg:flex",
        sidebarCollapsed ? "w-[3.75rem]" : "w-[15rem]",
      )}
      aria-label="Seitennavigation"
    >
      <div
        className={clsx(
          "flex items-center gap-2 border-b border-separator px-2 py-2.5",
          sidebarCollapsed ? "justify-center" : "px-3",
        )}
      >
        <SiteBrandMark
          collapsed={sidebarCollapsed}
          showName={!sidebarCollapsed}
          className={sidebarCollapsed ? "flex-1 justify-center" : "min-w-0 flex-1"}
          nameClassName="text-sm"
        />
        <button
          type="button"
          onClick={toggleSidebar}
          className="rounded-lg p-1.5 text-label-secondary hover:bg-fill hover:text-label"
          aria-label={sidebarCollapsed ? "Navigation erweitern" : "Navigation einklappen"}
        >
          {sidebarCollapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
        </button>
      </div>

      <div className="flex flex-1 flex-col gap-1 overflow-y-auto overscroll-contain p-2">
        <button
          type="button"
          onClick={openCommandPalette}
          className={clsx(
            "mb-1 flex items-center gap-2 rounded-lg border border-separator bg-grouped px-2.5 py-2 text-sm text-label-secondary transition hover:bg-fill hover:text-label",
            sidebarCollapsed && "justify-center px-2",
          )}
          title="Schnellsuche (Strg+K)"
        >
          <Search size={16} aria-hidden />
          {!sidebarCollapsed ? (
            <>
              <span className="flex-1 text-left">Suchen…</span>
              <kbd className="hidden rounded border border-separator px-1 text-[0.625rem] font-medium xl:inline">
                ⌘K
              </kbd>
            </>
          ) : null}
        </button>

        {favoriteItems.length > 0 && !sidebarCollapsed ? (
          <section className="mb-2">
            <p className="px-2.5 py-1 text-[0.625rem] font-semibold uppercase tracking-wide text-label-tertiary">
              Favoriten
            </p>
            {favoriteItems.map((item) => (
              <SidebarLink
                key={`fav-${item.key}`}
                href={item.href}
                label={item.label}
                icon={item.icon}
                badgeKey={item.badgeKey}
                collapsed={sidebarCollapsed}
              />
            ))}
          </section>
        ) : null}

        {recentItems.length > 0 && !sidebarCollapsed ? (
          <section className="mb-2">
            <p className="px-2.5 py-1 text-[0.625rem] font-semibold uppercase tracking-wide text-label-tertiary">
              Zuletzt
            </p>
            {recentItems.map((item) => (
              <SidebarLink
                key={`recent-${item.key}`}
                href={item.href}
                label={item.label}
                icon={item.icon}
                collapsed={sidebarCollapsed}
              />
            ))}
          </section>
        ) : null}

        <section>
          {!sidebarCollapsed ? (
            <p className="px-2.5 py-1 text-[0.625rem] font-semibold uppercase tracking-wide text-label-tertiary">
              Rezeption
            </p>
          ) : null}
          {primary.map((item) => (
            <SidebarLink
              key={item.key}
              href={item.href}
              label={item.label}
              icon={item.icon}
              badgeKey={item.badgeKey}
              collapsed={sidebarCollapsed}
              description={item.description}
            />
          ))}
        </section>

        {moreGroups.map((group) => (
          <section key={group.key} className="mt-2">
            {!sidebarCollapsed ? (
              <p className="px-2.5 py-1 text-[0.625rem] font-semibold uppercase tracking-wide text-label-tertiary">
                {group.label}
              </p>
            ) : null}
            {group.items.map((item) => (
              <SidebarLink
                key={item.key}
                href={item.href}
                label={item.label}
                icon={item.icon}
                collapsed={sidebarCollapsed}
              />
            ))}
          </section>
        ))}
      </div>
    </aside>
  );
}
